import { useState, useMemo } from "react";
import { Slider } from "./ui/slider";
import {
  sipFutureValue,
  stepUpSip,
  lumpsumFutureValue,
  swpSimulate,
  sipThenSwp,
  retirementPlan,
  bondMetrics,
  fmtINR,
  fmtCompact,
} from "../lib/finance";
import { Disclaimer } from "./ui-kit";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const NAVY = "#112C50";
const ORANGE = "#F1771E";

export const Control = ({ label, value, onChange, min, max, step, format, testid }) => (
  <div className="mb-6" data-testid={testid}>
    <div className="flex justify-between items-baseline mb-3">
      <label className="font-sans text-sm text-offblack">{label}</label>
      <span className="font-data text-sm text-navy font-medium">{format ? format(value) : value}</span>
    </div>
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={(v) => onChange(v[0])}
      className="[&_[role=slider]]:bg-orange [&_[role=slider]]:border-orange [&>span>span]:bg-orange"
    />
  </div>
);

const ResultBox = ({ label, value, big }) => (
  <div>
    <div className="font-sans text-xs uppercase tracking-[0.18em] text-slate mb-1">{label}</div>
    <div className={`font-data ${big ? "text-4xl md:text-5xl" : "text-2xl"} text-navy`}>{value}</div>
  </div>
);

export const Chart = ({ data, keys }) => (
  <div className="h-56 mt-6" data-testid="calc-chart">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ left: -10, right: 0, top: 5, bottom: 0 }}>
        <defs>
          <linearGradient id="gOrange" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ORANGE} stopOpacity={0.35} />
            <stop offset="100%" stopColor={ORANGE} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gNavy" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={NAVY} stopOpacity={0.25} />
            <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
        <XAxis dataKey="year" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#5B6672" }} tickLine={false} axisLine={false} />
        <YAxis tickFormatter={(v) => fmtCompact(v).replace("₹", "")} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#5B6672" }} tickLine={false} axisLine={false} width={44} />
        <Tooltip
          formatter={(v) => fmtINR(v)}
          contentStyle={{ fontFamily: "IBM Plex Mono", fontSize: 12, border: "1px solid #eee", borderRadius: 0 }}
          labelFormatter={(l) => `Year ${l}`}
        />
        {keys.map((k) => (
          <Area key={k.key} type="monotone" dataKey={k.key} name={k.name} stroke={k.color} strokeWidth={2} fill={k.fill} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const Shell = ({ children, output }) => (
  <div className="grid lg:grid-cols-2 gap-0 border border-navy/10 bg-white">
    <div className="p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-navy/10">{children}</div>
    <div className="p-8 lg:p-10 bg-navy-deep text-white flex flex-col justify-between">{output}</div>
  </div>
);

// ————————————————— MF Calculator (SIP / Lumpsum / SIP+SWP / SWP) —————————————————
export function MFCalculator() {
  const [mode, setMode] = useState("sip");
  const modes = [
    { id: "sip", label: "SIP" },
    { id: "lumpsum", label: "Lumpsum" },
    { id: "sipswp", label: "SIP + SWP" },
    { id: "swp", label: "SWP" },
  ];

  // SIP
  const [amount, setAmount] = useState(15000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);
  const [stepUp, setStepUp] = useState(10);
  // Lumpsum
  const [lump, setLump] = useState(500000);
  // SWP
  const [corpus, setCorpus] = useState(5000000);
  const [withdraw, setWithdraw] = useState(30000);
  const [swpYears, setSwpYears] = useState(20);

  const result = useMemo(() => {
    if (mode === "sip") {
      const step = stepUpSip(amount, rate, years, stepUp);
      return {
        big: fmtINR(step.fv),
        invested: step.invested,
        gain: step.fv - step.invested,
        data: step.series.map((s) => ({ year: s.year, Value: s.value, Invested: s.invested })),
        keys: [
          { key: "Invested", name: "Invested", color: NAVY, fill: "url(#gNavy)" },
          { key: "Value", name: "Value", color: ORANGE, fill: "url(#gOrange)" },
        ],
        summary: `Monthly SIP of ${fmtINR(amount)} stepping up ${stepUp}%/yr at ${rate}% for ${years} yrs`,
      };
    }
    if (mode === "lumpsum") {
      const ls = lumpsumFutureValue(lump, rate, years);
      return {
        big: fmtINR(ls.fv),
        invested: lump,
        gain: ls.fv - lump,
        data: ls.series.map((s) => ({ year: s.year, Value: s.value })),
        keys: [{ key: "Value", name: "Value", color: ORANGE, fill: "url(#gOrange)" }],
        summary: `Lumpsum of ${fmtINR(lump)} at ${rate}% for ${years} yrs`,
      };
    }
    if (mode === "swp") {
      const swp = swpSimulate(corpus, rate, withdraw, swpYears);
      return {
        big: fmtINR(swp.finalCorpus),
        invested: corpus,
        gain: swp.totalWithdrawn,
        gainLabel: "Total withdrawn",
        investedLabel: "Starting corpus",
        depletion: swp.depletionMonth,
        data: swp.series.map((s) => ({ year: s.year, Value: s.value })),
        keys: [{ key: "Value", name: "Corpus", color: ORANGE, fill: "url(#gOrange)" }],
        summary: `Withdrawing ${fmtINR(withdraw)}/mo from ${fmtINR(corpus)} at ${rate}% for ${swpYears} yrs`,
      };
    }
    // sipswp
    const combo = sipThenSwp(amount, rate, years, withdraw, swpYears);
    return {
      big: fmtINR(combo.finalCorpus),
      invested: combo.corpusAtRetirement,
      investedLabel: "Corpus at end of SIP",
      gain: combo.totalWithdrawn,
      gainLabel: "Total withdrawn",
      depletion: combo.depletionMonth,
      data: combo.series.map((s) => ({ year: s.year, Value: s.value })),
      keys: [{ key: "Value", name: "Corpus", color: ORANGE, fill: "url(#gOrange)" }],
      summary: `SIP ${fmtINR(amount)}/mo for ${years} yrs, then withdraw ${fmtINR(withdraw)}/mo for ${swpYears} yrs`,
    };
  }, [mode, amount, rate, years, stepUp, lump, corpus, withdraw, swpYears]);

  return (
    <div data-testid="mf-calculator">
      <div className="flex flex-wrap gap-2 mb-8">
        {modes.map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            data-testid={`mf-mode-${m.id}`}
            className={`px-5 py-2.5 font-sans text-sm transition-colors ${
              mode === m.id ? "bg-navy text-white" : "border border-navy/15 text-navy hover:border-navy"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <Shell
        output={
          <>
            <div>
              <ResultBox label={mode === "swp" || mode === "sipswp" ? "Corpus remaining" : "Projected value"} value={result.big} big />
              <div className="grid grid-cols-2 gap-6 mt-8">
                <ResultBox label={result.investedLabel || "Total invested"} value={fmtCompact(result.invested)} />
                <ResultBox label={result.gainLabel || "Est. gains"} value={fmtCompact(result.gain)} />
              </div>
              {result.depletion && (
                <p className="mt-6 font-data text-xs text-orange">
                  ⚠ Corpus depletes at month {result.depletion} (year {(result.depletion / 12).toFixed(1)}).
                </p>
              )}
              <Chart data={result.data} keys={result.keys} />
            </div>
            <Disclaimer className="!text-white/50 mt-6" />
          </>
        }
      >
        {(mode === "sip" || mode === "sipswp") && (
          <>
            <Control label="Monthly SIP" value={amount} onChange={setAmount} min={1000} max={200000} step={1000} format={fmtINR} testid="mf-amount" />
            {mode === "sip" && (
              <Control label="Annual step-up" value={stepUp} onChange={setStepUp} min={0} max={25} step={1} format={(v) => v + "%"} testid="mf-stepup" />
            )}
          </>
        )}
        {mode === "lumpsum" && (
          <Control label="Investment amount" value={lump} onChange={setLump} min={10000} max={10000000} step={10000} format={fmtINR} testid="mf-lump" />
        )}
        {(mode === "swp" || mode === "sipswp") && (
          <Control label="Monthly withdrawal" value={withdraw} onChange={setWithdraw} min={5000} max={500000} step={1000} format={fmtINR} testid="mf-withdraw" />
        )}
        {mode === "swp" && (
          <Control label="Starting corpus" value={corpus} onChange={setCorpus} min={100000} max={50000000} step={100000} format={fmtCompact} testid="mf-corpus" />
        )}
        <Control label="Expected return (p.a.)" value={rate} onChange={setRate} min={4} max={20} step={0.5} format={(v) => v + "%"} testid="mf-rate" />
        {(mode === "sip" || mode === "lumpsum" || mode === "sipswp") && (
          <Control label={mode === "sipswp" ? "SIP years" : "Time period"} value={years} onChange={setYears} min={1} max={40} step={1} format={(v) => v + " yrs"} testid="mf-years" />
        )}
        {(mode === "swp" || mode === "sipswp") && (
          <Control label="Withdrawal years" value={swpYears} onChange={setSwpYears} min={1} max={40} step={1} format={(v) => v + " yrs"} testid="mf-swpyears" />
        )}
        <p className="font-data text-xs text-slate mt-2">{result.summary}</p>
      </Shell>
    </div>
  );
}

// ————————————————— Retirement Calculator —————————————————
export function RetirementCalculator() {
  const [currentAge, setCurrentAge] = useState(32);
  const [retireAge, setRetireAge] = useState(60);
  const [life, setLife] = useState(85);
  const [expense, setExpense] = useState(60000);
  const [inflation, setInflation] = useState(6);
  const [preReturn, setPreReturn] = useState(12);
  const [postReturn, setPostReturn] = useState(8);
  const [existing, setExisting] = useState(1000000);

  const r = useMemo(
    () =>
      retirementPlan({
        currentAge,
        retireAge,
        lifeExpectancy: life,
        monthlyExpense: expense,
        inflation,
        preReturn,
        postReturn,
        currentInvestments: existing,
      }),
    [currentAge, retireAge, life, expense, inflation, preReturn, postReturn, existing]
  );

  return (
    <div data-testid="retirement-calculator">
      <Shell
        output={
          <>
            <div>
              <ResultBox label="Required retirement corpus" value={fmtCompact(r.requiredCorpus)} big />
              <div className="grid grid-cols-2 gap-6 mt-8">
                <ResultBox label="Required monthly SIP" value={fmtINR(r.requiredSip)} />
                <ResultBox label="Future value of existing" value={fmtCompact(r.fvExisting)} />
              </div>
              <div className="mt-6 space-y-1 font-data text-xs text-white/60">
                <p>Monthly expense at {retireAge}: {fmtINR(r.futureMonthly)}</p>
                <p>Shortfall to fund: {fmtCompact(r.shortfall)}</p>
                <p>Accumulation: {r.yearsToRetire} yrs · Withdrawal: {r.withdrawalYears} yrs</p>
              </div>
            </div>
            <Disclaimer className="!text-white/50 mt-6" />
          </>
        }
      >
        <Control label="Current age" value={currentAge} onChange={setCurrentAge} min={18} max={55} step={1} testid="ret-currentage" />
        <Control label="Retirement age" value={retireAge} onChange={setRetireAge} min={45} max={70} step={1} testid="ret-retireage" />
        <Control label="Life expectancy" value={life} onChange={setLife} min={70} max={100} step={1} testid="ret-life" />
        <Control label="Monthly expense today" value={expense} onChange={setExpense} min={15000} max={500000} step={5000} format={fmtINR} testid="ret-expense" />
        <Control label="Existing investments" value={existing} onChange={setExisting} min={0} max={50000000} step={100000} format={fmtCompact} testid="ret-existing" />
        <Control label="Inflation" value={inflation} onChange={setInflation} min={3} max={10} step={0.5} format={(v) => v + "%"} testid="ret-inflation" />
        <Control label="Return before retirement" value={preReturn} onChange={setPreReturn} min={6} max={16} step={0.5} format={(v) => v + "%"} testid="ret-prereturn" />
        <Control label="Return after retirement" value={postReturn} onChange={setPostReturn} min={4} max={12} step={0.5} format={(v) => v + "%"} testid="ret-postreturn" />
      </Shell>
    </div>
  );
}

// ————————————————— Bond Calculator (vs Fixed Deposit) —————————————————
export function BondCalculator() {
  const [face, setFace] = useState(1000);
  const [price, setPrice] = useState(960);
  const [coupon, setCoupon] = useState(8.5);
  const [years, setYears] = useState(5);
  const [fdRate, setFdRate] = useState(7);

  const b = useMemo(() => bondMetrics({ face, price, couponRate: coupon, years }), [face, price, coupon, years]);
  const diff = b.ytm - fdRate;

  return (
    <div data-testid="bond-calculator">
      <Shell
        output={
          <>
            <div>
              <ResultBox label="Approx. yield to maturity (YTM)" value={b.ytm.toFixed(2) + "%"} big />
              <div className="grid grid-cols-2 gap-6 mt-8">
                <ResultBox label="Current yield" value={b.currentYield.toFixed(2) + "%"} />
                <ResultBox label="Annual coupon" value={fmtINR(b.annualCoupon)} />
              </div>
              <div className="mt-8 p-5 border border-white/15">
                <div className="font-sans text-xs uppercase tracking-[0.18em] text-white/50 mb-2">vs Fixed Deposit ({fdRate}%)</div>
                <div className={`font-data text-2xl ${diff >= 0 ? "text-orange" : "text-white"}`}>
                  {diff >= 0 ? "+" : ""}{diff.toFixed(2)}% {diff >= 0 ? "in favour of bond" : "in favour of FD"}
                </div>
              </div>
            </div>
            <Disclaimer className="!text-white/50 mt-6">
              *Approximate YTM using a standard shortcut formula; actual YTM requires solving for IRR. FD interest is taxable; bond taxation varies. Illustrative only, not investment advice.
            </Disclaimer>
          </>
        }
      >
        <Control label="Face value" value={face} onChange={setFace} min={100} max={100000} step={100} format={fmtINR} testid="bond-face" />
        <Control label="Purchase price" value={price} onChange={setPrice} min={100} max={100000} step={100} format={fmtINR} testid="bond-price" />
        <Control label="Coupon rate" value={coupon} onChange={setCoupon} min={1} max={15} step={0.1} format={(v) => v + "%"} testid="bond-coupon" />
        <Control label="Years to maturity" value={years} onChange={setYears} min={1} max={30} step={1} format={(v) => v + " yrs"} testid="bond-years" />
        <Control label="Comparable FD rate" value={fdRate} onChange={setFdRate} min={3} max={12} step={0.1} format={(v) => v + "%"} testid="bond-fdrate" />
      </Shell>
    </div>
  );
}
